import React from 'react';

/**
 * ErrorBoundary: catches render-time + lifecycle errors in any subtree.
 *
 * - Reports to console + optional onError callback (wire to Sentry / posthog later).
 * - Shows a soft fallback UI; user can retry without full page reload.
 * - Use AppErrorBoundary at the route level; route-specific ones can wrap volatile widgets.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
    if (typeof this.props.onError === 'function') {
      try { this.props.onError(error, info); } catch { /* swallow logging errors */ }
    }
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === 'function') this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      if (typeof this.props.fallback === 'function') {
        return this.props.fallback({ error: this.state.error, reset: this.reset });
      }
      if (this.props.fallback) return this.props.fallback;
      return (
        <div role="alert" className="min-h-[40vh] flex flex-col items-center justify-center p-6 text-center">
          <h2 className="text-lg font-medium mb-2">Something went wrong.</h2>
          <p className="text-sm opacity-70 mb-4 max-w-md">
            We hit an unexpected error. Try again, or refresh the page if it persists.
          </p>
          <button
            onClick={this.reset}
            className="h-10 px-4 rounded-[10px] border border-current text-sm font-medium"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
